import React, { useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { Section } from '../../../../content-sections/ui/components/Section';
import {
	DeleteIcon,
	FixedGridItem,
	Row,
	StarIcon,
	StyledRow,
	TextButton,
} from '../../../../../ui';
import { LicensePlateNumber } from '../../../../auth/shared/form-fields/LicensePlateNumber';
import { ConfirmLicensePlateNumber } from '../../../../auth/shared/form-fields/ConfirmLicensePlateNumber';
import { VehicleDescription } from '../../../../auth/shared/form-fields/VehicleDescription';
import { useVehicleForm } from '../../ui/vehicle-form/helpers/useVehicleForm';
import { VehicleFormData } from '../../ui/vehicle-form/helpers/useVehicleFormSchema';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { ButtonWithLoader } from '../../../../../ui/components/Button/ButtonWithLoader';
import { useVehicleStores } from '../stores/useVehicleStores';
import { NewVehicle } from '../../../../shared/api/gateway/types/NewVehicle';
import { CustomCheckbox } from '../../../../../ui/components/Checkbox/CustomCheckbox';
import { useTranslation } from '../../../../shared/i18n/translate/useTranslation';
import { IVehicle } from '../../../../shared/api/gateway/models/Vehicle';
import { Color } from '../../../../shared/styles/Color';
import { trackAddVehicle } from '../../shared/analytics';
import { trackEditVehicle } from '../../shared/analytics/trackEditVehicle';
import { StyledLicensePlateRequest } from '../../../../parking/ui/booking-steps/details/sections';

export interface IVehicleFormProps {
	closeDialog: () => void;
	onDelete?: () => void;
	currentVehicle?: IVehicle;
	handleSuccess?: () => void;
	onError?: (error: Error) => void;
	analyticsScreenParamValue?: string;
	setSelectedVehicle?: (vehicle: IVehicle) => void;
}

export const VehiclesForm = observer(
	({
		closeDialog,
		currentVehicle,
		onDelete,
		handleSuccess,
		setSelectedVehicle,
		onError,
		analyticsScreenParamValue,
	}: IVehicleFormProps) => {
		const { t } = useTranslation();

		const getFavoriteVehicleCheckboxLabel = () => {
			if (currentVehicle) {
				return t('add_vehicle_form_set_as_default_label');
			}
			return t('add_vehicle_form_default_vehicle_label');
		};
		const checkboxLabel = getFavoriteVehicleCheckboxLabel();

		const form = useVehicleForm();
		const [isSubmitting, setIsSubmitting] = useState(false);

		const { getVehicleStore, addVehicleStore, editVehicleStore } =
			useVehicleStores();

		const { value } = getVehicleStore;
		const vehicles = value || [];
		const isFirstVehicle = vehicles.length === 0;

		const onSubmit = async ({
			description,
			licensePlateNumber,
			favorite,
		}: VehicleFormData) => {
			const newVehicle: NewVehicle = {
				description,
				licensePlate: licensePlateNumber,
				favorite,
			};

			try {
				setIsSubmitting(true);

				if (currentVehicle) {
					await editVehicleStore.load([currentVehicle.vehicleId, newVehicle]);

					trackEditVehicle();
					handleSuccess?.();
				} else {
					const updatedVehicle = isFirstVehicle
						? { ...newVehicle, favorite: true }
						: newVehicle;

					const addedVehicle = await addVehicleStore.load(updatedVehicle);

					if (addedVehicle?.favorite && setSelectedVehicle) {
						setSelectedVehicle(addedVehicle);
					}

					trackAddVehicle(analyticsScreenParamValue || 'Unknown');

					handleSuccess?.();
				}

				getVehicleStore.load();

				closeDialog();
			} catch (error) {
				onError?.(error as Error);
			} finally {
				setIsSubmitting(false);
			}
		};

		return (
			<FormProvider {...form}>
				<StyledVehicleForm noValidate onSubmit={form.handleSubmit(onSubmit)}>
					<Section>
						<Row>
							<VehicleDescription
								defaultValue={currentVehicle?.description}
								disabled={isSubmitting}
							/>
						</Row>
						<Row>
							<LicensePlateNumber
								defaultValue={currentVehicle?.licensePlate}
								disabled={isSubmitting}
							/>
							<StyledLicensePlateRequest>
								{t('add_vehicle_form_license_plate_support_text')}
							</StyledLicensePlateRequest>
						</Row>
						<Row>
							<ConfirmLicensePlateNumber
								defaultValue={currentVehicle?.licensePlate}
								disabled={isSubmitting}
							/>
						</Row>
					</Section>

					<CustomCheckbox
						name="favorite"
						id="favorite"
						label={checkboxLabel}
						checked={isFirstVehicle || currentVehicle?.favorite}
						disabled={
							isFirstVehicle || currentVehicle?.favorite || isSubmitting
						}
						icon={<StarIcon aria-hidden />}
					/>

					<hr />

					{currentVehicle && (
						<StyledDeleteButton onClick={onDelete}>
							<DeleteIcon aria-hidden />
							<p>{t('account_vehicles_delete_vehicle')}</p>
						</StyledDeleteButton>
					)}

					<StyledSaveVehicle>
						<ButtonWithLoader
							type="submit"
							disabled={isSubmitting}
							isLoading={isSubmitting}
						>
							{t('add_vehicle_form_confirm_button_label')}
						</ButtonWithLoader>
					</StyledSaveVehicle>
				</StyledVehicleForm>
			</FormProvider>
		);
	},
);

const StyledVehicleForm = styled.form`
	${FixedGridItem} {
		margin-left: 0;
		margin-top: 0;
		width: 100%;
		margin-bottom: 20px;
	}

	${StyledRow} {
		margin-left: 0;
		margin-top: 0;
		margin-bottom: 0px;
	}
`;

const StyledDeleteButton = styled(TextButton)`
	display: flex;
	align-items: center;
	gap: 12px;
	padding-inline: unset;
	color: ${Color.ErrorDark};

	&:hover {
		color: ${Color.ErrorDark};
	}

	p {
		margin-bottom: -2px;
	}

	svg {
		g {
			fill: ${Color.ErrorDefault};
		}
	}
`;

const StyledSaveVehicle = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;

	button {
		min-width: 138px;
	}
`;
